<template>
  <div>
    <Alert
      v-if="errorText !== ''"
      :text="errorText"
      type="danger"
      dismissable="false"
    />

    <div class="row">
      <div class="col-12" v-if="isSuperadmin">
        <div class="has-icon-left mb-1">
          <select class="form-control" v-model="country" @change="getStats()">
            <option value="all" selected>All Countries...</option>
            <option
              v-for="(country, index) in countries"
              :value="country"
              :key="index"
            >
              {{ countryName(country) }}
            </option>
          </select>
          <div class="form-control-position">
            <em class="bx bx-map font-medium-1"></em>
          </div>
        </div>
      </div>
      <div class="col-xl-4 col-md-6 col-12 dashboard-latest-update">
        <GenderChart :genders="genders" v-if="genders" />
        <LocationsList :locations="locations" v-if="locations" />
        <ProposalsChart :proposals="proposals" v-if="proposals" />
        <ContractsChart
          :contracts="contracts"
          v-if="contracts && contracts.total"
        />
      </div>
      <div class="col-xl-8 col-md-6 col-12">
        <div class="col-12">
          <UsersChart :users="types" :ages="ages" v-if="types && ages" />
          <ContractIssuesChart
            :issues="contractIssues"
            v-if="contractIssues && contractIssues.total"
          />
          <ContractPaymentsChart
            :contractPayments="contractPayments"
            v-if="contractPayments && contractPayments.total"
          />
          <PropertiesChart :properties="properties" v-if="properties" />
          <LifetimeStatsChart
            :lifetimeStats="lifetimeStats"
            v-if="lifetimeStats"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "@/app-assets/css/pages/dashboard-analytics.css";
import "@/app-assets/js/scripts/extensions/ext-component-block-ui.js";
import { getCountryName } from "@/app-assets/js/get-countries.js";
import Alert from "@/components/Alerts";
import GenderChart from "@/components/Dashboard/GenderChart";
import UsersChart from "@/components/Dashboard/UsersChart";
import LocationsList from "@/components/Dashboard/LocationsList";
import PropertiesChart from "@/components/Dashboard/PropertiesChart";
import ProposalsChart from "@/components/Dashboard/ProposalsChart";
import ContractsChart from "@/components/Dashboard/ContractsChart";
import ContractIssuesChart from "@/components/Dashboard/ContractIssuesChart";
import ContractPaymentsChart from "@/components/Dashboard/ContractPaymentsChart";
import LifetimeStatsChart from "@/components/Dashboard/LifetimeStatsChart";
import isEmpty from 'lodash/isEmpty';

export default {
  name: "DashboardPage",
  components: {
    Alert,
    GenderChart,
    UsersChart,
    LocationsList,
    PropertiesChart,
    ProposalsChart,
    ContractsChart,
    ContractIssuesChart,
    ContractPaymentsChart,
    LifetimeStatsChart,
  },
  data() {
    return {
      errorText: "",
      genders: null,
      types: null,
      ages: null,
      locations: null,
      properties: null,
      country: "all",
      countries: [],
      proposals: null,
      contracts: null,
      contractIssues: null,
      contractPayments: null,
      lifetimeStats: null,
    };
  },
  methods: {
    async getLifetimeStats() {
      this.lifetimeStats = null;

      try {
        const params = new URLSearchParams();
        const res = [];

        if (this.country !== "all") {
          params.append("country", this.country.toLowerCase());
        }

        this.errorText = "";

        let resBuffer = await this.$apiCall.get("/v1/aggregate", {
          params: params,
        });

        resBuffer = resBuffer.data.lifetimeStats;

        for (let [key, value] of Object.entries(resBuffer)) {
          res.push(resBuffer[key]);
        }

        const merged = this.mergeObjects(res);

        if (!isEmpty(merged)) {
          this.lifetimeStats = merged;
        }
      } catch (error) {
        this.errorText = `<strong>Ops!</strong> ${error.response.data.message}`;
      }
    },

    mergeObjects(data) {
      const result = {};

      data.forEach((val) => {
        for (let [key, value] of Object.entries(val)) {
          if (result[key]) {
            result[key] += value;
          } else {
            result[key] = value;
          }
        }
      });

      return result;
    },

    async getStats() {
      this.genders = null;
      this.types = null;
      this.ages = null;
      this.locations = null;
      this.properties = null;
      this.proposals = null;
      this.contracts = null;
      this.contractIssues = null;
      this.contractPayments = null;

      this.getLifetimeStats();

      $.blockUI({
        message: '<div class="bx bx-revision icon-spin font-medium-2"></div>',
        overlayCSS: {
          backgroundColor: "#fff",
          opacity: 0.8,
          cursor: "wait",
        },
        css: {
          border: 0,
          padding: 0,
          backgroundColor: "transparent",
        },
      });
      try {
        const params = new URLSearchParams();

        if (this.country !== "all") {
          params.append("country", this.country.toLowerCase());
        }

        this.errorText = "";
        let res = await this.$apiCall.get("/v1/aggregate/current", {
          params: params,
        });
        res = res.data.currentStats;

        const response = this.getResponse(res);
        const users = response.users;
        const properties = response.properties;
        const genders = users.genders.counts;
        const types = users.types.counts;
        const ages = users.ages;
        const locations = users.locations.counts;
        const proposals = response.proposals;
        const contracts = response.contracts;
        const contractIssues = response.contractIssues;
        const contractPayments = response.contractPayments;

        this.genders = {};
        this.types = {};

        genders.forEach((gender) => {
          for (const [key, value] of Object.entries(gender)) {
            this.genders[key] = value;
          }
        });

        types.forEach((type) => {
          for (const [key, value] of Object.entries(type)) {
            this.types[key] = value;
          }
        });

        // Calculate Banned Users
        const bannedData = users.banned.counts;
        let bannedCount = 0;

        bannedData.forEach((data) => {
          for (const [key, value] of Object.entries(data)) {
            if (key === "banned") {
              bannedCount += value;
            }
          }
        });

        this.types.banned = bannedCount;

        this.ages = ages;
        this.locations = locations;
        this.properties = properties;
        this.proposals = proposals;
        this.contracts = contracts;
        this.contractIssues = contractIssues;
        this.contractPayments = contractPayments;

        $.unblockUI();
      } catch (error) {
        $.unblockUI();
        this.errorText = `<strong>Ops!</strong> ${error.response.data.message}`;
      }
    },

    getResponse(res) {
      let response = {
        users: {},
        properties: {},
        proposals: {},
        contracts: {},
        contractIssues: {},
        contractPayments: {},
      };
      let count = 0;

      for (const [key, value] of Object.entries(res)) {
        count++;
        if (isEmpty(response.users)) {
          response.users = { ...value.users };
        } else {
          const users = value.users;
          const types = users.types.counts;
          const ages = users.ages.counts;
          const genders = users.genders.counts;
          const locations = users.locations.counts;
          const banned = users.banned.counts;

          response = this.sumData(response, types, "types", "users");
          response = this.sumData(response, ages, "ages", "users");
          response = this.sumData(response, genders, "genders", "users");
          response = this.sumData(response, locations, "locations", "users");
          response = this.sumData(response, banned, "banned", "users");
          response.users.ages.average += users.ages.average;
        }

        if (isEmpty(response.proposals)) {
          response.proposals = { ...value.proposals };
        } else {
          const proposals = value.proposals;
          const paymentTypes = proposals.paymentTypes.counts;
          const creationStatuses = proposals.creationStatuses.counts;

          response.proposals.averageContractDuration +=
            value.proposals.averageContractDuration;

          response.proposals.averagePaymentRecurrence +=
            value.proposals.averagePaymentRecurrence;

          response.proposals.total += value.proposals.total;

          response = this.sumData(
            response,
            paymentTypes,
            "paymentTypes",
            "proposals"
          );

          response = this.sumData(
            response,
            creationStatuses,
            "creationStatuses",
            "proposals"
          );
        }

        // Contracts Sum
        if (isEmpty(response.contracts)) {
          response.contracts = { ...value.contracts };
        } else {
          const contracts = value.contracts;
          const paymentTypes = contracts.paymentTypes.counts;

          response.contracts.averageContractDuration +=
            value.contracts.averageContractDuration;

          response.contracts.averagePaymentRecurrence +=
            value.contracts.averagePaymentRecurrence;

          response.contracts.total += value.contracts.total;

          response = this.sumData(
            response,
            paymentTypes,
            "paymentTypes",
            "contracts"
          );
        }

        // Contracts Payments Sum
        if (isEmpty(response.contractPayments)) {
          response.contractPayments = { ...value.contractPayments };
        } else {
          const contractPayments = value.contractPayments;
          const statuses = contractPayments.statuses.counts;
          const averageConfirmedValues =
            contractPayments.averageConfirmedValues;

          response.contractPayments.total += value.contractPayments.total;

          response.contractPayments.averageConfirmedPaymentsPerContract +=
            value.contractPayments.averageConfirmedPaymentsPerContract;

          response.contractPayments.averagePaymentsPerContract +=
            value.contractPayments.averagePaymentsPerContract;

          response = this.sumData(
            response,
            statuses,
            "statuses",
            "contractPayments"
          );

          for (const [key, value] of Object.entries(averageConfirmedValues)) {
            console.log(key, value);
            if (!averageConfirmedValues.hasOwnProperty(key)) {
              averageConfirmedValues[key] = value;
            } else {
              averageConfirmedValues[key] += value;
            }
          }
        }

        // Contract Issues Sum
        if (isEmpty(response.contractIssues)) {
          response.contractIssues = { ...value.contractIssues };
        } else {
          const contractIssues = value.contractIssues;
          const statuses = contractIssues.statuses.counts;

          response = this.sumData(
            response,
            statuses,
            "statuses",
            "contractIssues"
          );

          response.contractIssues.total += value.contractIssues.total;
          response.contractIssues.totalNonUrgent +=
            value.contractIssues.totalNonUrgent;
          response.contractIssues.totalResolvedNonUrgent +=
            value.contractIssues.totalResolvedNonUrgent;
          response.contractIssues.totalResolvedUrgent +=
            value.contractIssues.totalResolvedUrgent;
          response.contractIssues.totalUrgent +=
            value.contractIssues.totalUrgent;
          response.contractIssues.averageTTRNonUrgentMins +=
            value.contractIssues.averageTTRNonUrgentMins;
          response.contractIssues.averageTTRUrgentMins +=
            value.contractIssues.averageTTRUrgentMins;
        }

        if (isEmpty(response.properties)) {
          response.properties = { ...value.properties };
        } else {
          const properties = value.properties;
          const propertyTypes = properties.types.counts;
          const propertyTypesPerCity = properties.typesPerCity;
          const locations = properties.locations.counts;

          response.properties.typesPerCity = Object.assign(
            response.properties.typesPerCity,
            propertyTypesPerCity
          );

          response = this.sumData(
            response,
            propertyTypes,
            "types",
            "properties"
          );
          response = this.sumData(
            response,
            locations,
            "locations",
            "properties"
          );
        }
      }

      // Ages Average
      response.users.ages.average = response.users.ages.average / count;

      // Proposals Average
      response.proposals.averageContractDuration =
        response.proposals.averageContractDuration / count;

      response.proposals.averagePaymentRecurrence =
        response.proposals.averagePaymentRecurrence / count;

      // Contracts Average
      response.contracts.averageContractDuration =
        response.contracts.averageContractDuration / count;

      response.contracts.averagePaymentRecurrence =
        response.contracts.averagePaymentRecurrence / count;

      // Contract Issues Average
      response.contractIssues.averageTTRNonUrgentMins =
        response.contractIssues.averageTTRNonUrgentMins / count;

      response.contractIssues.averageTTRUrgentMins =
        response.contractIssues.averageTTRUrgentMins / count;

      // Contract Payments Average
      response.contractPayments.averageConfirmedPaymentsPerContract =
        response.contractPayments.averageConfirmedPaymentsPerContract / count;

      response.contractPayments.averagePaymentsPerContract =
        response.contractPayments.averagePaymentsPerContract / count;

      for (const [key, value] of Object.entries(
        response.contractPayments.averageConfirmedValues
      )) {
        response.contractPayments.averageConfirmedValues[key] =
          response.contractPayments.averageConfirmedValues[key] / count;
      }

      // console.log(response);
      return response;
    },

    sumData(response, data, field, target) {
      data.forEach((type) => {
        for (const [key, value] of Object.entries(type)) {
          const currentTypes = response[target][field].counts;
          let add = true;

          currentTypes.forEach((currentType, index) => {
            for (const [k, v] of Object.entries(currentType)) {
              if (k === key) {
                currentTypes[index][k] = v + value;
                add = false;
              }
            }
          });

          if (add) {
            currentTypes.push(type);
          }
        }
      });

      return response;
    },
    countryName(element) {
      if (element) {
        return getCountryName(element.toUpperCase());
      }
    },
    getActiveCountries() {
      const countries = localStorage.getItem("activeCountries");

      this.countries =
        countries && countries != "" ? countries.split(",") : ["LB", "JO"];
    },
  },

  computed: {
    isSuperadmin() {
      return this.$store.state.currentUser.user.type === "superadmin";
    },
  },

  mounted() {
    this.getActiveCountries();
    this.getStats();
  },
};
</script>

<style scoped>
select {
  width: 250px;
}
</style>